export const checkPageFilter = (currentUrl, pageFilter, pageFilterType) => {
  // If any parameter is missing, consider the filter passed.
  if (!currentUrl || !pageFilter || !pageFilterType) {
    return true;
  }

  // Helper to remove trailing slashes.
  const removeTrailingSlash = (url) => url.replace(/\/$/, '');

  // Generates a regex from a match string by replacing dynamic segments (e.g., {id}).
  const generateRegex = (match) => {
    if (!match) return null;
    try {
      // If match starts with "/", remove it to work with Regex
      if (match.startsWith('/')) {
        match = match.substring(1);
      }

      // Replace dynamic patterns {anything} with a placeholder first
      var dynamicPattern = /{\w+}/g;
      var regexPattern = match.replace(dynamicPattern, '___PLACEHOLDER___');

      // Escape special regex characters in the URL pattern
      regexPattern = regexPattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      // Replace our placeholder with the actual regex pattern
      regexPattern = regexPattern.replace(/___PLACEHOLDER___/g, '[^&?]+');
      var pattern = new RegExp(regexPattern, 'i');

      return pattern;
    } catch (e) {
      return null;
    }
  };

  // Checks if the provided URL matches the given match string.
  const isMatchingUrl = (url, match) => {
    if (!url || !match) return false;
    try {
      // First, a simple substring check.
      if (url.includes(match)) {
        return true;
      }
    } catch (e) {
      // Continue to regex matching.
    }
    const regex = generateRegex(match);
    if (!regex) return false;
    try {
      return regex.test(url);
    } catch (e) {
      return false;
    }
  };

  let matched = false;

  // Check matching based on the provided pageFilterType.
  switch (pageFilterType) {
    case 'is':
      matched = removeTrailingSlash(currentUrl) === removeTrailingSlash(pageFilter);
      break;
    case 'isnot':
      matched = removeTrailingSlash(currentUrl) !== removeTrailingSlash(pageFilter);
      break;
    case 'contains':
      matched = isMatchingUrl(currentUrl, pageFilter);
      break;
    case 'startswith':
      matched = currentUrl.startsWith(pageFilter);
      break;
    case 'endswith':
      matched = currentUrl.endsWith(pageFilter);
      break;
    case 'notcontains':
      matched = !isMatchingUrl(currentUrl, pageFilter);
      break;
    default:
      matched = false;
  }

  return matched;
};

// Exclusion (negative) rule types. These express "hide here" and must all
// hold simultaneously, so they are combined with AND. Everything else is a
// positive "show here" rule and is combined with OR.
const NEGATIVE_FILTER_TYPES = ['notcontains', 'isnot'];

// A rule without a value is an unfinished draft: the dashboard's "+ Add page
// rule" button inserts `{ pageFilter: '', pageFilterType }` before the user
// types anything. Such rules must be dropped, matching the server's
// buildPageRuleConditionGroups. Keeping one would hit checkPageFilter's
// "any parameter missing => passed" guard, which as a positive rule satisfies
// the OR allow-list and shows the action on EVERY page.
// Mirrors the server's `rule.pageFilter && rule.pageFilter.length > 0` verbatim
// — deliberately no trimming: a whitespace-only value is kept on both sides and
// simply never matches any URL (server never sends, SDK never shows).
const hasPageFilterValue = (rule) =>
  Boolean(rule && rule.pageFilter && rule.pageFilter.length > 0);

export const checkPageRules = (currentUrl, action) => {
  const configuredRules = action.pageRules && action.pageRules.length > 0
    ? action.pageRules
    : (action.pageFilter ? [{ pageFilter: action.pageFilter, pageFilterType: action.pageFilterType }] : []);

  const rules = configuredRules.filter(hasPageFilterValue);
  if (rules.length === 0) return true;

  const positiveRules = rules.filter(r => !NEGATIVE_FILTER_TYPES.includes(r.pageFilterType));
  const negativeRules = rules.filter(r => NEGATIVE_FILTER_TYPES.includes(r.pageFilterType));

  // Positive rules are an allow-list: show if the page matches ANY of them.
  const positivesPass = positiveRules.length === 0
    ? true
    : positiveRules.some(r => checkPageFilter(currentUrl, r.pageFilter, r.pageFilterType));

  // Negative rules are a deny-list: only show if the page passes EVERY
  // exclusion. Combining exclusions with OR (the previous behaviour) made any
  // multi-page exclusion a tautology that always resolved to "show".
  const negativesPass = negativeRules.length === 0
    ? true
    : negativeRules.every(r => checkPageFilter(currentUrl, r.pageFilter, r.pageFilterType));

  return positivesPass && negativesPass;
};
